import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId, FIRM } from "@/lib/site";
import { useLang } from "@/lib/lang";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === "/";
  const { t, lang, setLang } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (!isHome) {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
    } else {
      setTimeout(() => scrollToId(id), open ? 250 : 0);
    }
  };

  const light = isHome && !scrolled && !open;

  const LangToggle = ({ className = "" }) => (
    <div data-testid="lang-toggle" className={`flex items-center gap-1 font-mono text-[10px] ${className}`}>
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-1.5 py-1 tracking-[0.15em] transition-colors ${
            lang === l
              ? "text-gold"
              : light
                ? "text-alabaster/55 hover:text-alabaster"
                : "text-navy/50 hover:text-navy"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <header
      data-testid="nav-header"
      className={`fixed inset-x-0 top-9 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? "border-b border-hairline bg-alabaster/85 backdrop-blur-md"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-6 lg:px-12">
        <div className={light ? "[&_span_span]:!text-alabaster [&_span_span]:!text-alabaster/60" : ""}>
          <Logo dark={scrolled || open || !isHome} onNavigate={go} />
        </div>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigazione principale">
          {[
            { id: "studio", label: t.nav.studio },
            { id: "aree", label: t.nav.aree },
            { id: "team", label: t.nav.team },
            { id: "sede", label: t.nav.sede },
            { id: "metodo", label: t.nav.metodo },
            { id: "news", label: t.nav.news },
            { id: "contatti", label: t.nav.contatti },
          ].map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`link-gold whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                light ? "text-alabaster/85 hover:text-gold" : "text-navy hover:text-gold-dark"
              }`}
            >
              {l.label}
            </button>
          ))}
          {FIRM.phone && (
            <a
              data-testid="nav-phone"
              href={`tel:${FIRM.phoneHref}`}
              className={`hidden items-center gap-2 font-mono text-[11px] tracking-[0.12em] transition-colors xl:flex ${
                light ? "text-alabaster/85 hover:text-gold" : "text-navy hover:text-gold-dark"
              }`}
            >
              <Phone size={13} strokeWidth={1.5} />
              {FIRM.phone}
            </a>
          )}
          <LangToggle className="hidden md:flex" />
          <button
            data-testid="nav-cta-button"
            onClick={() => go("contatti")}
            className="group relative overflow-hidden border border-gold bg-gold px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-navy transition-colors duration-500 hover:bg-transparent hover:text-gold"
          >
            {t.nav.cta}
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="flex h-11 w-11 items-center justify-center lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          aria-expanded={open}
        >
          {open ? (
            <X className="h-6 w-6 text-gold" strokeWidth={1.25} />
          ) : (
            <Menu className={`h-6 w-6 ${light ? "text-alabaster" : "text-navy"}`} strokeWidth={1.25} />
          )}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-0 -z-10 flex flex-col justify-center bg-navy-deep px-8 lg:hidden"
          >
            <div className="flex flex-col gap-6">
              {[
                { id: "studio", label: t.nav.studio },
                { id: "aree", label: t.nav.aree },
                { id: "team", label: t.nav.team },
                { id: "sede", label: t.nav.sede },
                { id: "metodo", label: t.nav.metodo },
                { id: "contatti", label: t.nav.contatti },
              ].map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.07 * i + 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.id)}
                  className="text-left font-serif text-4xl font-medium text-alabaster transition-colors hover:text-gold"
                >
                  <span className="mr-4 font-mono text-xs text-gold">0{i + 1}</span>
                  {l.label}
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="mt-4 flex items-center gap-6"
              >
                <button
                  onClick={() => go("contatti")}
                  className="w-max border border-gold bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-navy"
                >
                  {t.nav.cta}
                </button>
                <LangToggle className="text-alabaster" />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
